import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store';
import LanguageSelector from '../components/LanguageSelector';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page py-12 px-4 relative">
      {/* Language selector in top right */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-sm">
        {/* The mark sits on the page rather than on the card, so identity is established
            before the form rather than inside it. Decorative: the heading below already
            names the app, so alt is empty to avoid a duplicate announcement. */}
        <div className="flex flex-col items-center mb-7">
          <img src="/happyeco-logo.png" alt="" className="h-24 w-24" />
          <h1 className="text-2xl font-semibold tracking-tight text-ink-900 mt-4">
            {t('common.appName')}
          </h1>
          <p className="text-sm text-ink-400 mt-1.5">{t('auth.signInToAccount')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-card p-7 space-y-5">
          {error && (
            <div className="rounded-xl bg-red-50 px-4 py-3.5 text-sm text-red-800 ring-1 ring-inset ring-red-200">
              {error}
            </div>
          )}

          <label className="block">
            <span className="block text-[13px] font-medium text-ink-600 mb-1.5">
              {t('auth.email')}
            </span>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              className="w-full px-3.5 py-2.5 border border-hair rounded-[10px] text-sm bg-white
                placeholder:text-ink-400 focus:outline-none focus:ring-2
                focus:ring-happy-green-700/25 focus:border-happy-green-700"
              placeholder="admin@example.com"
            />
          </label>

          <label className="block">
            <span className="block text-[13px] font-medium text-ink-600 mb-1.5">
              {t('auth.password')}
            </span>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="w-full px-3.5 py-2.5 border border-hair rounded-[10px] text-sm bg-white
                placeholder:text-ink-400 focus:outline-none focus:ring-2
                focus:ring-happy-green-700/25 focus:border-happy-green-700"
              placeholder="••••••••"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center rounded-[10px] px-4 py-2.5
              text-sm font-medium bg-happy-green-800 text-white shadow-btn
              hover:bg-happy-green-900 disabled:bg-happy-green-300 disabled:shadow-none
              disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t('auth.signingIn') : t('auth.signIn')}
          </button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-ink-400 hover:text-happy-green-800 transition-colors"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
        </form>

        <p className="mt-5 text-center text-xs text-ink-400">
          {t('auth.adminAccessOnly')}
        </p>
      </div>
    </div>
  );
}
